import { getProductRouterPath } from '../../utils';
import { axiosProvider, unwrapResponseData } from '@/app/core/axios';
import { FileRows } from '@/app/features/products/common/components';
import { getCurrentFeatureCode } from '@/app/core/auth';
import {
  ScoresLookupsApiResponse,
  BundleLookupsApiResponse,
  GroupedAttributesLookupsApiResponse,
  AttributesLookupsApiResponse,
  DemographicLookupsApiResponse,
  BundleAttributeLookupsApiResponse,
  DataDictionaryVersionApiResponse,
  TrendviewFieldLookupsApiResponse,
  AddonProductLookupsApiResponse,
  FileLayoutApiGatewayResponse,
  FileLayout,
  SaveLayoutApiGatewayResponse,
  FullAttributesLookupsApiResponse,
  FullAttrAttributeLookupsApiResponse,
  getShortNameForProductToLower,
  HeaderInfoApiGatewayResponse,
  InputRemoteFileApiResponse,
  InputRemoteFilesApiGatewayResponse,
  InputLabelsMapApiResponse,
  SSARProgram,
  OutputLayoutField,
  OutputLayoutFieldSources,
  OutputLayoutFieldsResponse,
  HeaderInfoData,
} from '@ssar-app/domain';
import ms from 'ms';
import moment from 'moment';
import { CancelToken } from 'axios';

const cacheTtl = ms('1 week');
export const getScores = (
  version: string,
  subCode: string,
): Promise<ScoresLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<ScoresLookupsApiResponse>(
      `/lookups/scores?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getTrendviewFields = (
  version: string,
  subCode: string,
): Promise<TrendviewFieldLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<TrendviewFieldLookupsApiResponse>(
      `/lookups/trendview?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getAddonProducts = (
  version: string,
  subCode: string,
): Promise<AddonProductLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<AddonProductLookupsApiResponse>(
      `/lookups/addon-products?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getBundles = (
  version: string,
  subCode: string,
): Promise<BundleLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<BundleLookupsApiResponse>(
      `/lookups/bundles?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getGroupedAttributes = (
  version: string,
  subCode: string,
): Promise<GroupedAttributesLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<GroupedAttributesLookupsApiResponse>(
      `/lookups/attributes/grouped?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getAttributes = (
  version: string,
  subCode: string,
): Promise<AttributesLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<AttributesLookupsApiResponse>(
      `/lookups/attributes?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getFullAttributes = (
  version: string,
  subCode: string,
): Promise<FullAttributesLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<FullAttributesLookupsApiResponse>(
      `/lookups/full-attributes?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getFullAttrAttributes = (
  version: string,
  subCode: string,
): Promise<FullAttrAttributeLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<FullAttrAttributeLookupsApiResponse>(
      `/lookups/complete-attribute-set-groups?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getDemographics = (
  version: string,
  subCode: string,
): Promise<DemographicLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<DemographicLookupsApiResponse>(
      `/lookups/demographics?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getBundleAttributes = (
  version: string,
  subCode: string,
): Promise<BundleAttributeLookupsApiResponse> => {
  return axiosProvider.apiClient
    .get<BundleAttributeLookupsApiResponse>(
      `/lookups/bundle-attributes?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: cacheTtl,
        },
      },
    )
    .then(unwrapResponseData);
};

export const getInputFilesFromRemote = async (
  subCode: string,
  productName: string,
  uploadTimeLB: string = moment().subtract(30, 'days').format('x'),
  uploadTimeUB: string = moment().format('x'),
): Promise<InputRemoteFilesApiGatewayResponse> => {
  return axiosProvider.apiClient
    .get<InputRemoteFilesApiGatewayResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/remote-files/list-files?subCode=${subCode}&uploadTimeLB=${uploadTimeLB}&uploadTimeUB=${uploadTimeUB}`,
    )
    .then(unwrapResponseData);
};

export const getInputFileHeadersFromRemote = async (
  fileId: string,
): Promise<HeaderInfoApiGatewayResponse> => {
  return axiosProvider.apiClient
    .get<HeaderInfoApiGatewayResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/remote-files/file-headers?fileId=${fileId}`,
    )
    .then((response) => {
      const apiResp: HeaderInfoApiGatewayResponse = response.data;
      const hdrData: HeaderInfoData | undefined = apiResp?.data;
      if (
        hdrData?.headersInfo &&
        hdrData.headersInfo.detectedHeaders.length >
          hdrData.headersInfo.actualHeaders.length
      ) {
        hdrData.headersInfo.detectedHeaders =
          hdrData.headersInfo.detectedHeaders.slice(
            0,
            hdrData.headersInfo.actualHeaders.length,
          );
      }
      return {
        errors: [],
        message: '',
        status: 'Success',
        data: hdrData,
      };
    });
};

export const getInputFileFromRemote = async (
  fileId: string,
  count: number,
  random: boolean,
): Promise<InputRemoteFileApiResponse> => {
  return axiosProvider.apiClient
    .get<InputRemoteFileApiResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/remote-files/file-content?fileId=${fileId}&count=${count}&random=${random}`,
    )
    .then(unwrapResponseData);
};

export const getHeadersInfoFromCmdLine = (
  fileRows: FileRows,
): Promise<HeaderInfoApiGatewayResponse> => {
  const payload = JSON.stringify(fileRows);
  return axiosProvider.apiClient
    .post<HeaderInfoApiGatewayResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/get-header-info`,
      {
        payload,
      },
    )
    .then(unwrapResponseData);
};

export const getVersionFromDate = (
  asOfDate: number,
): Promise<DataDictionaryVersionApiResponse> => {
  return axiosProvider.apiClient
    .get<DataDictionaryVersionApiResponse>(
      `/program/${encodeURIComponent(
        getProductRouterPath(),
      )}/aws/get-version-from-date?date=${asOfDate}`,
    )
    .then(unwrapResponseData);
};

export const sendMail = (
  subject: string,
  body: Record<string, string>,
): Promise<string> => {
  return axiosProvider.apiClient
    .post<string>(
      `/program/${encodeURIComponent(getProductRouterPath())}/aws/send-email`,
      {
        subject,
        body: JSON.stringify(body),
      },
    )
    .then((_res) => {
      return `Message sent successfully.`;
    })
    .catch((_res) => {
      return `Could not send message`;
    });
};

export const getInputLabelsMap = (
  version: string,
  subCode: string,
): Promise<InputLabelsMapApiResponse> => {
  return axiosProvider.apiClient
    .get<InputLabelsMapApiResponse>(
      `/lookups/input-labels/as-map?version=${version}&subcode=${subCode}`,
      {
        cache: {
          ttl: ms('1 week'),
        },
      },
    )
    .then(unwrapResponseData);
};

export const getLayout = (
  fileId: string,
): Promise<FileLayoutApiGatewayResponse> => {
  return axiosProvider.apiClient
    .get<FileLayoutApiGatewayResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/remote-files/get-layout?fileId=${fileId}`,
      {
        cache: false,
      },
    )
    .then(unwrapResponseData);
};

export const saveLayout = (
  fileId: string,
  layout: FileLayout[],
): Promise<SaveLayoutApiGatewayResponse> => {
  return axiosProvider.apiClient
    .post<SaveLayoutApiGatewayResponse>(
      `/program/${getShortNameForProductToLower(
        getCurrentFeatureCode() || '',
      )}/remote-files/save-layout`,
      {
        fileId: fileId,
        layout: layout,
      },
    )
    .then(unwrapResponseData);
};

export const buildOutputsFromProgramWizardModelForAscendQuest = async (
  subCode: string,
  version: string,
  model: SSARProgram,
  excludeMandatoryFields = false,
  cancelToken?: CancelToken,
): Promise<OutputLayoutField[]> => {
  const fieldSources: OutputLayoutFieldSources = {
    scores: model.scoresAndAddonProducts?.scores,
    demographics: model.demographics,
    inputFields: model?.inputFile?.fieldsInfo?.fields,
    attributes: model.attributesAndBundles?.attributes,
    bundles: model.attributesAndBundles?.bundles,
    reports: model.reports,
  };
  return axiosProvider.apiClient
    .post<OutputLayoutFieldsResponse>(
      `/program/${encodeURIComponent(
        getShortNameForProductToLower(getCurrentFeatureCode() || ''),
      )}/build-output-field-layout`,
      {
        subCode,
        fieldSources,
        version,
        excludeMandatoryFields,
      },
      cancelToken ? { cancelToken } : {},
    )
    .then((a) => {
      return a.data?.data || [];
    });
};

export const getAutoTimer = async (): Promise<number> => {
  return 30 * 1000; // 30 sec
};
